import type { Insurance } from "~/types/shipment";

/**
 * Currencies the platform holds conversion rates for. Anything else (USD, most notably)
 * has no rate row, so `GET /insurance` fails rather than mislabelling DKK amounts.
 */
export const INSURANCE_CURRENCIES = ["DKK", "EUR", "NOK", "SEK"];

export const insuranceCurrencyIsSupported = (currency?: string | null) =>
  !currency || INSURANCE_CURRENCIES.includes(currency.trim().toUpperCase());

export default () => {
  const loading = ref(false);
  /** API message when the products could not be fetched. There is no partial data to fall back to. */
  const error = ref<string | null>(null);

  /**
   * `currency` is the currency the shipment is being quoted in - it comes off the carrier
   * product, not off a locale or a user preference. Omit it and the API answers in DKK.
   * Every returned item carries its own `currency`; format each item with that field.
   */
  const fetchInsurances = async (
    object: Record<string, any>,
    currency?: string | null
  ): Promise<Insurance[] | null> => {
    loading.value = true;
    error.value = null;

    const carrier = object.carrier || object.carrier_product_service?.split("_")[0];
    const weight = Math.ceil(object.weight);

    const query = new URLSearchParams({ carrier, weight: String(weight) });
    if (currency) query.set("currency", currency.trim().toUpperCase());

    return homeFetch<Insurance[]>(`insurance/?${query.toString()}`, { silent: true })
      .then((response) => {
        // The API fails instead of guessing a rate - never fall back to unconverted amounts.
        if (response.status === "Error" || !response.data) {
          error.value = response.message ?? "";
          return null;
        }
        return response.data;
      })
      .catch((e) => {
        error.value = e?.data?.message ?? "";
        return null;
      })
      .finally(() => {
        loading.value = false;
      });
  };

  return {
    fetchInsurances,
    loading,
    error,
  };
};
